import { defineStore } from 'pinia'
import API from '../services/api';
import { METERING_TYPES } from "../pages/constants"
import currencySymbolName from "../pages/currency-symbol-name-map"
import { getProjectImageUrl, getUiFromStorage, setUiInStorage } from '../utils';
import {reverseGeocodeCoordinates} from '../components/googleMaps/googleMaps.js';
import { useMiscStore } from './misc.js';

export const useProjectStore = defineStore('project', {
    state: () => {
        return {
            id: null,
            lead_id: null,
            projectAddress: '',
            client_address: '',
            client_email_id: '',
            client_name: '',
            client_phone: '',
            created_at: '',
            created_by: {
                email: '',
                first_name: '',
                id: null,
                last_name: '',
            },
            name_of_profile_used: getUiFromStorage().profileType || 'currentProfile',
            consumption_details: {
                average_price_per_unit: 0,
                consumption_input_type: null,
                id: null,
                monthly_units: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                project: null,
                tariff_escalation_rate: 0,
                averageMonthlyConsumption: 0,
                annual_consumption: 0,
                zero_export_enabled: false,
                is_utility_rate_added: false,
                user_input_values: new Array(12).fill(null)
            },
            is_consumption_loading: false,
            all_consumption_profiles:[],
            selected_profile_obj:{},
            previous_selected_profile_obj:{},
            temp_updated_profile_obj:{},
            isProfileChangesCancelled:false,
            selectedProfileDescription: '',
            consumption_profile: 0,
            consumption: 0,
            geo_image: null,
            geo_image_thumbnail: null,
            latitude: null,
            longitude: null,
            name: '',
            organisation: null,
            zoom: '18',
            country: 91,
            country_details: {
                conversion_factor: '',
                country_code: '',
                currency: '',
                currency_code: '',
                id: '',
                name: '',
            },
            add_ons_availed:{},
            isCurrentUserAllowedToEdit: false,
            designs: [],
            weather: null,
            quota_type: null,
            AHJCode:'',
            AHJName:'',
            BuildingCode:'',
            ElectricCode:'',
            FireCode:'',
            Level:'',
            ResidentialCode:'',
            projectType: null,
            project_type:null,
            features:{},
            type_of_rate:'tou',
            site_survey_token: null,
            site_survey:[],
            permissionForLeadObject:{},
            isDesignListChanged:false,
            fullExportEnabled:'',
            applianceUpdateCounter:0,
            isValuesChanged:false,
            isConsumptionBeingCustomized: false,
            isConsumptionBeingEdited: false,
            showConsumptionPage: false,
            disable_monthly_electricity_bill: false,
            is_project_store_loaded: false,
            is_metering_type_mounted_run_for_genability: false,
            isBillUpdating:false,
            isProjectBeingLoadedInProjectSummary: false,
            documents:[],
            projectAddressEditFlag: true,
            address_update_count: null,
            allow_address_update: true,
            designOrderTariffFlag: false,
            projectPageLoaderGenability: false,
        }
    },
    getters: {
        GET_PROJECT_PAGE_LOADER_GENABILITY: (state) => state.projectPageLoaderGenability,
        GET_DESIGN_ORDER_TARIFF_FLAG: (state) => state.designOrderTariffFlag,
        GET_PROJECT_ADDRESS_UPDATE_COUNT: (state) => state.address_update_count,
        GET_PROJECT_ADDRESS_EDIT_FLAG: (state) => state.projectAddressEditFlag,
        GET_PROJECT_ADDRESS: (state) => state.projectAddress,
        GET_PROJECT_INFORMATION: ({
            id, name, client_name: clientName, client_email_id: clientEmail, client_phone: clientPhone,
            created_at: createdAt, client_address: clientAddress, created_by: createdBy,
            latitude, longitude, weather, quota_type: quotaType, country,
            country_details: countryDetails, project_type, type_of_rate, site_survey_token, site_survey
        }) => ({
            id,
            projectName: name,
            clientName,
            clientEmail,
            clientPhone,
            createdAt: createdAt.split('T')[0],
            clientAddress,
            createdBy: `${createdBy.first_name} ${createdBy.last_name}`,
            latitude,
            longitude,
            weather,
            quotaType,
            country,
            country_details: countryDetails,
            project_type,
            type_of_rate,
            site_survey_token,
            site_survey
        }),
        GET_SELECTED_CONSUMPTION_PROFILE : (state) =>{
            return state.selected_profile_obj;
        },
        GET_PROJECT_IMAGE_URL: (state) => {
            if (state.geo_image_thumbnail !== null) {
                return state.geo_image_thumbnail;
            }
            if (state.latitude !== null && state.longitude !== null) {
                return getProjectImageUrl(state.latitude, state.longitude, state.zoom);
            }
            return '';
        },
    
        GET_PROJECT_IMAGE_HREF: (state) => {
            if (state.latitude !== null && state.longitude !== null) {
                return `https://www.google.com/maps/@?api=1&map_action=map&center=${
                    state.latitude},${state.longitude}&zoom=${state.zoom}&basemap=satellite&layer=transit`;   
            }
            return '';
        },
    
        GET_PROJECT_CONSUMPTION_DETAILS: (state) => {
            let averageMonthlyUnitConsumption = 0;
            if (state.consumption_details.monthly_units !== null) {
                let total = 0;
                // eslint-disable-next-line no-plusplus
                for (let i = 0; i < state.consumption_details.monthly_units.length; i++) {
                    total += state.consumption_details.monthly_units[i];
                }
                averageMonthlyUnitConsumption = total / 12;
            }
            return {
                id: state.consumption_details.id,
                project: state.consumption_details.project,
                average_price_per_unit: state.consumption_details.average_price_per_unit || 0,
                annual_consumption: state.consumption_details.annual_consumption || 0,
                tariff_escalation_rate: state.consumption_details.tariff_escalation_rate || 0,
                average_export_price_per_unit: state.consumption_details.average_export_price_per_unit || 0,
                metering_type: state.consumption_details.metering_type || METERING_TYPES.NET_METERING,
                averageMonthlyConsumption: averageMonthlyUnitConsumption,
                utility_details : state.consumption_details.utility_details,
                zero_export_enabled: state.consumption_details.zero_export_enabled,
                full_export_enabled: state.consumption_details.full_export_enabled,
                utility_tariff_details : state.consumption_details.utility_tariff_details,
                estimated_energy_consumption:state.consumption_details.estimated_energy_consumption || 0,
                critical_load:state.consumption_details.critical_load || 0,
                is_utility_rate_added: state.consumption_details.is_utility_rate_added,
            };
        },
        GET_TYPE_OF_RATE:(state)=>{
            return state.type_of_rate;
        },
        GET_AHJ_DETAILS: (state) => {
            return {
                AHJCode : state.AHJCode,
                AHJName : state.AHJName,
                BuildingCode : state.BuildingCode,
                ElectricCode : state.ElectricCode,
                FireCode : state.FireCode,
                Level: state.Level,
                ResidentialCode : state.ResidentialCode,
            }
        },
        GET_PROJECT_UTILITY_DETAILS: (state)=>{
            return state.consumption_details.utility_details;
        },
        GET_PROJECT_UTILITY_TARIFF_DETAILS: (state)=>{
            return state.consumption_details.utility_tariff_details;
        },
        GET_CONSUMPTION_DETAILS: (state)=>{
            return state.consumption_details;
        },
        GET_DESIGNS_DETAILS: state => state.designs,

        GET_NAME_OF_PROFILE_USED: state => state.name_of_profile_used,
    
        GET_USER_PERMISSION: state => state.isCurrentUserAllowedToEdit,
    
        GET_DESIGNS_COUNT: state => state.designs.length,
    
        GET_PROJECT_TYPE: state => state,
    
        GET_CURRENCY_SYMBOL: state => currencySymbolName[state.country_details.currency_code] || state.country_details.currency_code,
    
        GET_TOTAL_FEATURES : state => state.features,
        
        GET_COUNTRY_DETAILS : state => state.country_details,

        GET_PERMISISON_OBJECT : state => state.permissionForLeadObject,
        
        GET_FULL_EXPORT_ENABLED : state => state.fullExportEnabled,

        GET_IS_PROJECT_STORE_LOADED : state => state.is_project_store_loaded,

        GET_IS_METERING_TYPE_MOUNTED_RUN_FOR_GENABILITY : state => state.is_metering_type_mounted_run_for_genability,
        
        isUsProject() {
            return this.country_details.country_code == "US"
        },

        isUSOrCanadaProject(){
            return this.isUsProject || this.country_details.country_code == "CA"
        }
    },
    actions: {
        SET_PROJECT_PAGE_LOADER_GENABILITY(boolVal){
            this.projectPageLoaderGenability = boolVal;
        },
        SET_DESIGN_ORDER_TARIFF_FLAG(){
            this.designOrderTariffFlag = !this.allow_address_update;
        },
        SET_PROJECT_ADDRESS_EDIT_FLAG() {
            //if this flag is true then we can edit the address of the project
            this.projectAddressEditFlag = this.allow_address_update ? this.address_update_count < 3 : false;
        },
        async SET_PROJECT_ADDRESS_FROM_LAT_LNG() {
            const latlng = new google.maps.LatLng(this.latitude, this.longitude);
            let newMapState = await reverseGeocodeCoordinates(latlng, false);
            this.projectAddress = newMapState.address;
            useMiscStore().UPDATE_GOOGLE_MAPS_STATE({
                address: this.projectAddress,
                coordinates: latlng,
            });
        },
        async GET_CURRENT_PROJECT(projectID) {
            try {
                this.SET_IS_PROJECT_STORE_LOADED(false);  //we are making this false and then true because we are checking this value in watcher so we are reseting the value
                const response = await API.PROJECTS.FETCH_PROJECT(
                    projectID,
                );
				this.SET_CURRENT_PROJECT(response.data);
                this.SET_IS_PROJECT_STORE_LOADED(true);
            }
            catch (e) {
                throw e;            
            }
        },
    
        async UPDATE_PROJECT_INFORMATION(payload) {
            try {
                const response = await API.PROJECTS.PATCH_PROJECT(this.id, payload);
                this.SET_CURRENT_PROJECT(response.data);
            } catch (e) {
                throw e;
            }
        },
    
        async UPDATE_PROJECT_CONSUMPTION_DETAILS(payload) {
            try {                                                                                                                                                                                                                                                                                                                                                                                                                                                                                       
                await API.PROJECT_CONSUMPTION_DETAILS.PATCH_CONSUMPTION_DETAILS(this.consumption_details.id, payload);                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      
                await this.GET_CURRENT_PROJECT(this.id);
            } 
            catch (e) { 
                throw e;                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      
            }                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                               
        },

        async RESET_CONSUMPTION_DETAIlS(query, payload) {
            try {
                const response = await API.PROJECT_CONSUMPTION_DETAILS.RESET_CONSUMPTION_DETAIlS(this.consumption_details.id, query, payload);
                this.UPDATE_CONSUMPTION_DETAILS_STORE(response.data)
                await this.GET_CURRENT_PROJECT(this.id);
            } catch (e) {
                throw e
            }
        },

        async FETCH_PERMISSION_OBJECT(projectId){
            try {                                                                                                                                                                                                                                                                                                                                                                                                                                                                                    
                const response = await API.PROJECTS.FETCH_PROJECT_PERMISSIONS_FOR_LEAD(projectId);
                this.SET_PERMISSION_OBJECT(response.data);
            } 
            catch (e) { 
                throw e;                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      
            }                
            
        },  
        async FETCH_SELECTED_PROFILE(profileId){
            try{
                const response =  await API.PROJECT_CONSUMPTION_DETAILS.FETCH_SPECIFIC_PROFILE(profileId); 
                this.selected_profile_obj = response.data;
                return response;
            }
            catch(e){
                console.error(e);
            }
        },
        UPDATE_WEATHER_ID(payload) {
            this.weather = payload.weatherID !== undefined ? payload.weatherID : this.weather;
        },
        UPDATE_IS_UTILITY_RATE_ADDED(isUtilityRateAdded){
            this.consumption_details.is_utility_rate_added = isUtilityRateAdded;
        },
        UPDATE_CONSUMPTION_DETAILS_STORE(payload) {
            this.consumption_details = payload;
        },
        UPDATE_ALL_AVAILABLE_FEATURES(featureJson){
            this.features = {... featureJson};
        },
        UPDATE_CONSUMPTION_INPUT_TYPE(payload) {
            this.consumption_details.consumption_input_type = payload;
        },
        UPDATE_CONSUMPTION_DETAILS(payload) {
            this.consumption_details.annual_consumption = payload.annual_consumption;
            this.consumption_details.annual_cost = payload.annual_cost;
            this.consumption_details.average_monthly_consumption = payload.average_monthly_consumption;
            this.consumption_details.average_monthly_cost = payload.average_monthly_cost;
            this.consumption_details.consumption = payload.consumption;
            this.consumption_details.consumption_profile = payload.consumption_profile;
            this.consumption_details.current_profile_details = payload.current_profile_details;
            this.consumption_details.future_profile_details = payload.future_profile_details;
            this.consumption_details.monthly_units = payload.monthly_units;
            this.consumption_details.user_input_values = payload.user_input_values;
            this.consumption_details.average_monthly_consumption = payload.average_monthly_consumption
            this.consumption_details.average_spring_monthly_consumption = payload.average_spring_monthly_consumption
            this.consumption_details.average_summer_monthly_consumption = payload.average_summer_monthly_consumption
            this.consumption_details.average_autumn_monthly_consumption = payload.average_autumn_monthly_consumption
            this.consumption_details.average_winter_monthly_consumption = payload.average_winter_monthly_consumption
        },
        IS_LOADING_STATE(val) {
            this.is_consumption_loading = val;
        },
        SHOW_CONSUMPTION_PAGE(val) {
            this.showConsumptionPage = val;
        },
        UPDATE_NAME_OF_PROFILE_USED(newProfile) {
            this.name_of_profile_used = newProfile;
            let ui = getUiFromStorage();
            ui.profileType = newProfile
            setUiInStorage(ui);
        },
        SET_PERMISSION_OBJECT(payload){
            this.permissionForLeadObject = payload
        },
        SET_PROJECT_ID(payload){
            this.id = payload.project;
        },
        DISABLE_MONTHLY_ELECTRICITY_BILL(val) {
            this.disable_monthly_electricity_bill = val
        },
        SET_CURRENT_PROJECT(payload) {
            this.id = payload.id;
            this.lead_id = payload.lead_id;
            this.name = payload.name;
            this.client_address = payload.client_address;
            this.client_email_id = payload.client_email_id;
            this.client_name = payload.client_name;
            this.client_phone = payload.client_phone;
            this.consumption_details = payload.consumption_details;
            this.consumption_profile = payload.consumption_profile;
            this.consumption = payload.consumption;
            this.geo_image = payload.geo_image;
            this.geo_image_thumbnail = payload.geo_image_thumbnail;
            this.latitude = payload.latitude;
            this.longitude = payload.longitude;
            this.organisation = payload.organisation;
            this.zoom = payload.zoom;
            this.country = payload.country;
            this.country_details = {
                conversion_factor: payload.country_details.conversion_factor,
                country_code: payload.country_details.country_code,
                currency: payload.country_details.currency,
                currency_code: payload.country_details.currency_code,
                id: payload.country_details.id,
                name: payload.country_details.name,
            };
            this.add_ons_availed = payload.add_ons_availed;
            this.designs = payload.designs;
            this.created_at = payload.created_at;
            if (typeof payload.created_by === 'object') {
                this.created_by = payload.created_by;
            }
            this.weather = payload.weather;
            this.quota_type = payload.quota_type;
            this.AHJCode = payload.AHJCode;
            this.AHJName = payload.AHJName;
            this.BuildingCode= payload.BuildingCode;
            this.ElectricCode = payload.ElectricCode;
            this.FireCode = payload.FireCode;
            this.Level = payload.Level;
            this.ResidentialCode = payload.ResidentialCode;
            this.project_type = payload.project_type;
            this.features = {...payload.features};
            this.type_of_rate = payload.type_of_rate;
            this.site_survey_token = payload.site_survey_token;
            this.site_survey = Array.isArray(payload.site_survey) ? [...payload.site_survey]: [];
            this.fullExportEnabled = payload.consumption_details.full_export_enabled;
            this.documents=payload.documents;

            // set projectAddress based on lat lng
            this.SET_PROJECT_ADDRESS_FROM_LAT_LNG();

            // set address_update_count key
            this.address_update_count = payload.address_update_count;
            this.allow_address_update = payload.allow_address_update;
            this.SET_PROJECT_ADDRESS_EDIT_FLAG();
            this.SET_DESIGN_ORDER_TARIFF_FLAG();
        },
        UPDATE_SITE_SURVEY(response, id) {
            const payload = {
                "token": response.data.token,
                "template": {
                    "id": response.data.template.id,
                    "name": response.data.template.name,
                    "type": response.data.template.type
                },
                'created_at': response.data.created_at,
                "is_completed": false,
                "is_editable": true,
                "user_guide_required": true,
            }
            this.site_survey.push(payload);
        },
        SET_USER_PERMISSION(payload) {
            this.isCurrentUserAllowedToEdit = payload;
        },
        CHANGE_DESIGN_LIST(){
            console.log("design list changed",this.isDesignListChanged)
            this.isDesignListChanged = ! this.isDesignListChanged;
        },
        UPDATE_SITE_SURVEY_TOKEN(value) {
            this.site_survey_token = value;
        },
        FULL_EXPORT_ENABLED(value) {
            this.fullExportEnabled = value;
        },
        UPDATE_OR_ADD_APPLIANCE_IN_STORE(payload,applianceId){
            let applianceLists = (this.consumption_details.appliances_details)|| [];
            let index;
            if(!applianceId){
                applianceLists.push(payload);
            }
            else{   
                index = applianceLists.findIndex((appliance)=>{
                   return appliance.id == applianceId
                })
                if(index>=0)
                    applianceLists[index] = payload;
            }
            this.consumption_details.appliances_details = applianceLists ;
            this.applianceUpdateCounter++;
        },
        REMOVE_APPLIANCE_FROM_STORE(applianceId){
            let applianceLists = this.consumption_details.appliances_details|| [];
            let index = applianceLists.findIndex((appliance)=>{
              return  appliance.id == applianceId
            })
            if(index>=0)
                applianceLists.splice(index,1);
            this.consumption_details.appliances_details = JSON.parse(JSON.stringify(applianceLists));
            this.applianceUpdateCounter++;
        },
        UPDATE_COUNTRY_CODE(countryCode)
        {
            this.country_details.country_code = countryCode;
        },
        SET_CONSUMPTION_DETAILS(consumptionDetails) {
            this.consumption_details = consumptionDetails
        },
        SET_IS_PROJECT_STORE_LOADED(isProjectStoreLoaded) {
            this.is_project_store_loaded = isProjectStoreLoaded
        },
        SET_IS_METERING_TYPE_MOUNTED_RUN_FOR_GENABILITY(isMeteringTypeMountedRunForGenability) {
            this.is_metering_type_mounted_run_for_genability = isMeteringTypeMountedRunForGenability
        },
        GET_CURRENT_DAILY_CONSUMPTION() {
            return parseFloat((this.consumption_details.annual_consumption/365).toFixed(2));
        },
        GET_FUTURE_DAILY_CONSUMPTION() {
            let dailyConsumption = this.consumption_details.annual_consumption/365;
            if(this.consumption_details.appliances_details && this.consumption_details.appliances_details.length){
                this.consumption_details.appliances_details.forEach(appliance => {
                    dailyConsumption += appliance.average_daily_consumption;
                });
            }
            else{
                dailyConsumption = 0;
            }
            return parseFloat(dailyConsumption.toFixed(2));
        }
    }
})